import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, Button, Spinner, Form, Table } from 'react-bootstrap';
import { formatDay } from '../utils/dates';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

// The factura a request is about (send to DGII, void, a note), as the server sends it with the link: client, lines, totals.
const InvoicePreview = ({ invoice }) => (
  <div className="mb-3">
    <div className="d-flex justify-content-between flex-wrap small text-muted mb-1">
      <span>Factura <strong style={{ color: 'var(--mm-text)' }}>{invoice.number}</strong>{invoice.tipoEcf ? ` · ${invoice.tipoEcf}` : ''}</span>
      <span>{formatDay(invoice.createdAt)}{invoice.dueDate ? ` · vence ${formatDay(invoice.dueDate)}` : ''}</span>
    </div>
    <div className="mb-2"><strong>{invoice.client?.name || 'Sin cliente'}</strong>{invoice.client?.rnc ? <span className="text-muted"> · RNC/Cédula {invoice.client.rnc}</span> : null}</div>
    <div className="table-responsive">
      <Table size="sm" className="mb-1">
        <thead><tr><th>Descripción</th><th className="text-center">Cant.</th><th className="text-right">Precio</th><th className="text-right">Total</th></tr></thead>
        <tbody>
          {invoice.items.map((it, i) => {
            const line = Math.abs(it.quantity * it.unitPrice);
            return (
              <tr key={i}>
                <td style={{ overflowWrap: 'anywhere' }}>{it.description}{it.itbisExento ? <span className="text-muted small"> (exento)</span> : null}</td>
                <td className="text-center">{Math.abs(it.quantity)}</td>
                <td className="text-right">{money(it.unitPrice)}</td>
                <td className="text-right">{money(line + (it.itbisExento ? 0 : line * it.itbisRate))}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
    <div className="text-right small">
      <div>Subtotal {money(Math.abs(invoice.subtotal))} · ITBIS {money(Math.abs(invoice.itbisTotal))}</div>
      <div style={{ fontSize: '1.1rem' }}>Total <strong style={{ color: 'var(--mm-cyan)' }}>{money(Math.abs(invoice.total))}</strong></div>
    </div>
    {invoice.notes && <div className="small text-muted mt-2" style={{ whiteSpace: 'pre-line' }}>Notas: {invoice.notes}</div>}
  </div>
);

// The page behind the Aprobar / Denegar buttons in the approval email. No login: the link's
// token is personal to one admin. The buttons in the email only open this page (mail scanners
// open every link, so a link must never decide anything by itself) — the decision is made by
// pressing a button here.
const ApprovalDecisionPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('decision'); // approve | deny
  const [view, setView] = useState(null); // request as the server describes it
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null); // the request after the decision
  const [denying, setDenying] = useState(preselected === 'deny'); // the reason box is open
  const [reason, setReason] = useState('');
  const [warning, setWarning] = useState(''); // why an approval couldn't be carried out (the request stays open)

  useEffect(() => {
    axios.get(`/api/approvals/public/${token}`)
      .then(r => setView(r.data))
      .catch(err => setProblem(err.response?.status === 404 ? 'Este enlace no es válido o ya no existe.' : 'No se pudo cargar la solicitud. Intenta de nuevo.'));
  }, [token]);

  const decide = async (decision) => {
    setBusy(decision);
    setWarning('');
    try {
      const { data } = await axios.post(`/api/approvals/public/${token}/decide`, { decision, ...(decision === 'deny' ? { reason: reason.trim() } : {}) });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 409) {
        // Already decided (nothing to do), or it couldn't be carried out and is still open (say why; the buttons stay).
        const data = err.response.data;
        setView(prev => ({ ...prev, ...data, canDecide: data.status === 'pending' }));
        if (data.status === 'pending') { setWarning(data.message || ''); setDenying(false); }
      }
      else setProblem(err.response?.data?.message || 'No se pudo registrar tu decisión.');
    } finally { setBusy(''); }
  };

  const current = result || view;
  const notes = current?.notes || null; // what to say about this action (server-provided)
  const say = (text) => (text || '').replace('{requester}', current?.requestedBy || '');
  const finalStatus = result ? result.status : null;
  const closedLabel = { approved: 'ya fue aprobada', denied: 'ya fue denegada', cancelled: 'fue cancelada por quien la pidió', expired: 'venció sin respuesta' };

  return (
    <div className="container py-5" style={{ maxWidth: view?.invoice ? 720 : 560 }}>
      <Card className="shadow-sm p-4">
        {!current && !problem && <div className="text-center text-muted"><Spinner animation="border" size="sm" /> Cargando solicitud...</div>}
        {problem && <div className="alert alert-danger mb-0">{problem}</div>}

        {current && !problem && (
          <>
            <div className="small text-muted text-uppercase" style={{ letterSpacing: '0.05em' }}>Aprobación requerida</div>
            <h3 className="mb-3" style={{ fontSize: '1.4rem' }}><strong>{current.requestedBy}</strong> pide autorización para:</h3>
            <div className="p-3 mb-3" style={{ border: '1px solid var(--mm-line)', borderLeft: '4px solid var(--mm-cyan)', borderRadius: 6, background: 'var(--mm-panel-2)' }}>
              <div className="small text-muted text-uppercase">{current.label}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{current.target?.label}</div>
              {current.target?.detail && <div className="text-muted">{current.target.detail}</div>}
              {current.summary && <div className="mt-2" style={{ overflowWrap: 'anywhere' }}>{current.summary}</div>}
            </div>
            {view?.invoice && <InvoicePreview invoice={view.invoice} />}

            {finalStatus && (
              <div className={`alert ${finalStatus === 'approved' ? 'alert-success' : 'alert-danger'} mb-0`}>
                <strong>{finalStatus === 'approved' ? 'Aprobaste esta solicitud.' : 'Denegaste esta solicitud.'}</strong>
                {finalStatus === 'denied' && current.reason && <div className="small mt-1">Motivo: “{current.reason}”</div>}
                <div className="small">{notes ? (finalStatus === 'approved' ? (result?.resultMessage || say(notes.approved)) : say(notes.denied)) : `${current.requestedBy} ya lo ve en su pantalla.`} Puedes cerrar esta ventana.</div>
              </div>
            )}

            {!finalStatus && current.canDecide && (
              <>
                {(warning || current.failure) && <div className="alert alert-warn small">{warning || `La última vez no se pudo completar: ${current.failure}`}</div>}
                <p className="small text-muted">Estás aprobando como <strong>{view?.approver}</strong>. {notes ? say(notes.waiting) : `${current.requestedBy} está esperando en su pantalla.`}</p>

                {!denying && (
                  <div className="d-flex gap-2">
                    <Button className="flex-fill" variant="success" disabled={!!busy} onClick={() => decide('approve')}>
                      {busy === 'approve' ? (notes?.approveBusy || 'Aprobando...') : 'Aprobar'}
                    </Button>
                    <Button className="flex-fill" variant="outline-danger" disabled={!!busy} onClick={() => setDenying(true)}>Denegar</Button>
                  </div>
                )}

                {denying && (
                  <>
                    <Form.Label className="d-block small text-muted mb-1">Motivo de la denegación <span className="text-muted">(opcional; {current.requestedBy} lo verá {notes?.reasonHint || 'en su pantalla'})</span></Form.Label>
                    <Form.Control as="textarea" rows={3} maxLength={300} autoFocus value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="Ej.: esta factura tiene el cliente equivocado; corrígela primero." />
                    <div className="small text-muted text-end">{reason.length}/300</div>
                    <div className="d-flex gap-2 mt-2">
                      <Button className="flex-fill" variant="danger" disabled={!!busy} onClick={() => decide('deny')}>
                        {busy === 'deny' ? 'Denegando...' : 'Confirmar denegación'}
                      </Button>
                      <Button variant="outline-secondary" disabled={!!busy} onClick={() => setDenying(false)}>Volver</Button>
                    </div>
                  </>
                )}
              </>
            )}

            {!finalStatus && !current.canDecide && (
              <div className="alert alert-danger mb-0">
                Esta solicitud {closedLabel[current.status] || 'ya no está pendiente'}
                {current.decidedBy ? <> (por <strong>{current.decidedBy}</strong>)</> : null}. No hace falta hacer nada.
                {current.status === 'denied' && current.reason && <div className="mt-1">Motivo: “{current.reason}”</div>}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ApprovalDecisionPage;
